import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import MarketMapPage from './pages/MarketMapPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ProcessPage from './pages/ProcessPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import DashboardRouter from './pages/dashboard/DashboardRouter';
import WattsonChat from './components/WattsonChat';

// SEO Component for dynamic meta tags
const SEOHandler = () => {
  const location = useLocation();

  useEffect(() => {
    const seoData = {
      '/': {
        title: 'Last Mile Advisors | Energy & Utility Cost Advisory Services',
        description: 'Reduce your energy and utility costs with Last Mile Advisors. Expert energy procurement, rate analysis, and cost optimization for commercial, industrial, and residential clients.',
        keywords: 'energy consulting, utility cost management, energy advisory services, commercial energy optimization, electricity savings, natural gas savings'
      },
      '/services': {
        title: 'Energy Advisory Services | Last Mile Advisors',
        description: 'Comprehensive energy and utility advisory services including rate analysis, cost optimization, regulatory review, and strategic energy planning for organizations.',
        keywords: 'utility rate analysis, energy cost optimization, regulatory review, strategic energy advisory, energy procurement services'
      },
      '/market-map': {
        title: 'Deregulated Energy Markets Map | Last Mile Advisors',
        description: 'Explore which US states have deregulated electricity and natural gas markets. Find energy savings opportunities in your state with Last Mile Advisors.',
        keywords: 'deregulated energy markets, energy choice states, electricity deregulation, natural gas deregulation, energy market map'
      },
      '/about': {
        title: 'About Last Mile Advisors | Independent Energy Advisory',
        description: 'Learn about Last Mile Advisors - your independent, vendor-neutral energy and utility cost advisory firm helping organizations reduce energy expenses.',
        keywords: 'about last mile advisors, energy consulting firm, independent energy advisory, utility cost experts'
      },
      '/process': {
        title: 'Our Process | Regulated vs Deregulated Energy Markets | Last Mile Advisors',
        description: 'See how buying energy in a deregulated market with Last Mile Advisors compares to traditional regulated markets. Save 10-30% on electricity and natural gas.',
        keywords: 'energy buying process, regulated vs deregulated energy, how to save on energy, energy procurement process, switch energy suppliers'
      },
      '/contact': {
        title: 'Contact Last Mile Advisors | Request a Consultation',
        description: 'Contact Last Mile Advisors to discuss your energy and utility cost challenges. Request a free consultation to learn how we can help you save.',
        keywords: 'contact energy consultant, energy savings consultation, utility cost help, energy advisory contact'
      },
      '/blog': {
        title: 'Energy Insights Blog | Last Mile Advisors',
        description: 'Expert insights on lowering energy costs, deregulated markets, fixed vs variable rates, and electricity procurement strategies from Last Mile Advisors.',
        keywords: 'energy blog, energy savings tips, deregulated energy insights, electricity procurement blog, business energy advice'
      }
    };

    // Blog post pages manage their own SEO tags (title, description, JSON-LD)
    if (location.pathname.startsWith('/blog/')) return;

    const currentSEO = seoData[location.pathname] || seoData['/'];
    document.title = currentSEO.title;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = currentSEO.description;

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = currentSEO.keywords;

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = currentSEO.title;

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.content = currentSEO.description;

  }, [location]);

  return null;
};

// Conditionally show header/footer/wattson (hide on dashboard, login, auth callback)
const AppLayout = () => {
  const location = useLocation();
  const isDashboard = location.pathname === '/dashboard';
  const isLogin = location.pathname === '/login';
  const isAuthCallback = location.pathname === '/auth/callback';
  const hideChrome = isDashboard || isAuthCallback;

  return (
    <>
      <SEOHandler />
      {!hideChrome && <Header />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/market-map" element={<MarketMapPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/process" element={<ProcessPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/dashboard" element={<DashboardRouter />} />
      </Routes>
      {!hideChrome && !isLogin && !isAuthCallback && <Footer />}
      {!hideChrome && !isLogin && !isAuthCallback && <WattsonChat />}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppLayout />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
